import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, TextField, Card, CardContent, Divider, Skeleton, List, ListItemAvatar, Avatar,
  ListItemText, IconButton, Badge, ListItemButton, Drawer
} from '@mui/material';
import {
  Search, Menu, ChevronLeft, Edit, PersonAdd, ChatBubbleOutline, ChevronRight
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../../../API';
import ChatScreen from '../../../Components/ChatScreen';
import useIsDesktop from '../../../Hooks/useIsDesktop';
import useAuth from '../../../Hooks/use-auth';
import { useCreatorAuth } from '../../../Hooks/creator-use-auth';
import useAlertDialog from '../../../Components/useAlertDialog';
import AlertDialog from '../../../Components/AlertDialog';
import CreateNewChatDialog from '../../../Components/ChatComponents/newchat';
import ParticipantsPanel from '../../../Components/ChatComponents/chatextra';
import UpdateConversationDetailsDialog from '../../../Components/ChatComponents/updatedetails';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
const BOTTOM_NAV_HEIGHT = 86;                              // matches AppShell
const SAFE_BOTTOM       = 'env(safe-area-inset-bottom,0)'; // iOS home-bar inset

export default function ConversationsHub() {
  const queryClient = useQueryClient();
  const isMobile = !useIsDesktop();
  const location = useLocation();
  const navigate = useNavigate();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  /* ---------------- state ---------------- */
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const [addParticipantOpen, setAddParticipantOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [selectedNewChatUsers, setSelectedNewChatUsers] = useState([]);
  const [updateDetailsDialogOpen, setUpdateDetailsDialogOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  /* ---------------- auth ---------------- */
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const { creatorToken } = useCreatorAuth();
  const userId = userInfo?.id || creatorToken?.creator_user?.username || null;

  /* ---------------- queries ---------------- */
  const {
    data: conversations = [],
    isLoading: isLoadingConversations,
    error: convError,
    refetch: refetchConversations
  } = useQuery(
    ['messages', 'userSentConversations'],
    () => API.conversations.fetchUserToUser(),
    { initialData: [] }
  );

  const {
    data: usersList = [],
    isLoading: isLoadingUsers
  } = useQuery(
    ['users'],
    () => API.users.publicList(),
    { refetchOnWindowFocus: false, initialData: [] }
  );

  /* ---------------- mutations ---------------- */
  const createNewChatMutation = useMutation(
    (params) => API.conversations.checkUserToUser(params),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['userSentConversations']);
        handleCloseNewChat();
        handleSelectConversation(res.conversation_id);
      },
      onError: (err) => {
        console.error('Error creating new conversation:', err);
        openDialog('Error', `Error creating conversation: ${err?.response?.data?.error || err.message}`);
      }
    }
  );

  const inviteByEmailMutation = useMutation(
    (emailsArray) => API.conversations.inviteByEmail(selectedConversationId, emailsArray),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userSentConversations']);
        queryClient.invalidateQueries(['messages', selectedConversationId]);
        handleCloseAddParticipant();
      },
      onError: (err) => {
        console.error('Error inviting by email:', err);
        openDialog('Error', `Error inviting to conversation: ${err?.response?.data?.error || err.message}`);
      }
    }
  );

  const updateConversationDetailsMutation = useMutation(
    (details) => API.conversations.updateConversationDetails(selectedConversationId, details),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['messages', 'userSentConversations']);
        if (selectedConversationId) {
          queryClient.invalidateQueries(['messages', selectedConversationId]);
        }
        setUpdateDetailsDialogOpen(false);
      },
      onError: (err) => {
        console.error('Error updating conversation details:', err);
      }
    }
  );

  /* ---------------- helpers ---------------- */
  const getConvoName = (convo) => {
    if (convo.display_name) return convo.display_name;
    if (convo.campaign_name) return convo.campaign_name;
    if (convo.partnership_name) return convo.partnership_name;
    const otherUser = convo.participants?.find((p) => p.id !== userId);
    return otherUser ? `${otherUser.first_name} ${otherUser.last_name}` : `Conversation #${convo.id}`;
  };

  const filteredConversations = useMemo(() => {
    if (!searchQuery) return conversations;
    const lowerQuery = searchQuery.toLowerCase();
    return conversations.filter((convo) =>
      getConvoName(convo).toLowerCase().includes(lowerQuery)
    );
  }, [conversations, searchQuery]);

  const adminUserOptions = usersList.map((u) => ({
    label: `${u.first_name} ${u.last_name}`,
    value: u.id
  }));

  const selectedConversation = conversations.find((c) => c.id === selectedConversationId);

  /* ---------------- ui handlers ------------- */
  const toggleLeftPanel = () => setIsLeftPanelCollapsed((p) => !p);

  const handleSelectConversation = (convoId) => {
    setSelectedConversationId(convoId);
    navigate(`?conversation=${convoId}`, { replace: true });
    if (isMobile) setIsLeftPanelCollapsed(true);
  };

  const handleBackToList = () => {
    setSelectedConversationId(null);
    navigate('', { replace: true });
    if (isLeftPanelCollapsed) setIsLeftPanelCollapsed(false);
    refetchConversations();
  };

  const handleClickAddParticipant = () => setAddParticipantOpen(true);
  const handleCloseAddParticipant = () => {
    setAddParticipantOpen(false);
    setInviteEmails('');
  };

  const handleOpenNewChat = () => setNewChatOpen(true);
  const handleCloseNewChat = () => {
    setNewChatOpen(false);
    setSelectedNewChatUsers([]);
  };

  const handleCreateOrFindChat = () => {
    if (!selectedNewChatUsers || !selectedNewChatUsers.length) return;
    const recipientIds = selectedNewChatUsers.map((u) => u.value);
    const params = recipientIds.length === 1 ? { recipient_id: recipientIds[0] } : { recipient_ids: recipientIds };
    createNewChatMutation.mutate(params);
  };

  const handleInviteByEmail = () => {
    const trimmed = inviteEmails.trim();
    if (!trimmed) return;
    const emailsArray = trimmed.split(',').map((e) => e.trim()).filter(Boolean);
    inviteByEmailMutation.mutate({ emails: emailsArray });
  };

  const handleOpenUpdateDetailsDialog = () => setUpdateDetailsDialogOpen(true);
  const handleCloseUpdateDetailsDialog = () => setUpdateDetailsDialogOpen(false);
  const handleSaveDetails = (updatedDetails) => {
    if (!selectedConversationId) return;
    updateConversationDetailsMutation.mutate(updatedDetails);
  };

  const handleToggleParticipants = () => setParticipantsOpen((prev) => !prev);

  /* ---------------- URL sync ---------------- */
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const convoId = searchParams.get('conversation');
    if (convoId) {
      setSelectedConversationId(Number(convoId));
      refetchConversations();
    }
  }, [location.search, refetchConversations]);

  /* ---------------- loading / error ---------------- */
  if (convError)
    return (
      <Box sx={{ p: 2, color: 'red' }}>
        Error: {convError.message}
      </Box>
    );

  /* ------------------------------------------------------------------ */
  /*  render                                                            */
  /* ------------------------------------------------------------------ */
  return (
    <Box
      sx={{
        display: 'flex',
        height: isMobile
          ? `calc(100dvh - ${BOTTOM_NAV_HEIGHT}px - ${SAFE_BOTTOM})`
          : '100dvh',
        width: '100%',
        overflow: 'hidden',
        bgcolor: 'background.default',
        /** 👇 keep the bottom-nav visible */
      }}
    >
      {/* Left Panel */}
      <Card
        sx={{
          m: 1,
          width: isLeftPanelCollapsed ? 0 : { xs: '100%', md: 400 },
          minWidth: isLeftPanelCollapsed ? 0 : { xs: '100%', md: 400 },
          height: '100%',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          position: { xs: 'absolute', md: 'relative' },
          zIndex: 1,
          left: isLeftPanelCollapsed ? '-100%' : 0,
          display: { xs: selectedConversationId ? 'none' : 'flex', md: 'flex' },
          flexDirection: 'column'
        }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Conversations</Typography>
            {!isMobile && (
              <IconButton onClick={toggleLeftPanel}>
                <ChevronRight />
              </IconButton>
            )}
          </Box>
          {/* Search bar */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
            }}
            sx={{ mb: 1 }}
          />
          {/* New chat button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenNewChat}
            fullWidth
          >
            Create New Chat
          </Button>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', pb: isMobile ? `calc(${BOTTOM_NAV_HEIGHT}px + ${SAFE_BOTTOM})` : 4 }}>
          <List>
            {isLoadingConversations ? (
              [...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={60} sx={{ m: 1 }} />
              ))
            ) : (
              filteredConversations.map((convo) => (
                <ListItemButton
                  key={convo.id}
                  onClick={() => handleSelectConversation(convo.id)}
                  selected={convo.id === selectedConversationId}
                >
                  <ListItemAvatar>
                    <Avatar src={convo.participants.find((user) => user.id !== userId)?.phphref} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography variant="subtitle1" noWrap>
                          {getConvoName(convo)}
                        </Typography>
                        <Badge
                          badgeContent={convo.unread_count}
                          color="primary"
                          max={999}
                          sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', minWidth: '18px', height: '18px' } }}
                        >
                          <ChatBubbleOutline fontSize="small" />
                        </Badge>
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {convo.message || 'No messages yet'}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </Box>
      </Card>

      {/* Right Panel */}
      <Card
        sx={{
          flex         : 1,
          height       : '100%',
          display      : 'flex',
          flexDirection: 'column',
          m            : { xs: 0, md: 1 },          // ← NEW
          borderRadius : { xs: 0, md: 2 }           // ← NEW
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isMobile && (
              <IconButton onClick={toggleLeftPanel}>
                {isLeftPanelCollapsed ? <Menu /> : <ChevronRight />}
              </IconButton>
            )}
            {isMobile && selectedConversationId && (
              <IconButton onClick={handleBackToList}>
                <ChevronLeft />
              </IconButton>
            )}
            <Typography variant="h6" noWrap>
              {selectedConversation
                ? selectedConversation.display_name || `Conversation #${selectedConversationId}`
                : 'Select a conversation'}
            </Typography>
            {selectedConversationId && (
              <IconButton onClick={handleOpenUpdateDetailsDialog} size="small">
                <Edit fontSize="small" />
              </IconButton>
            )}
          </Box>
          {selectedConversationId && (
            <IconButton onClick={handleToggleParticipants}>
              <PersonAdd />
            </IconButton>
          )}
        </Box>
        <Divider />
        {selectedConversationId ? (
          <ChatScreen conversationId={selectedConversationId} sx={{ flex: 1, minHeight: '0px' }} />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Select a conversation
            </Typography>
          </Box>
        )}
      </Card>

      {/* Add Participant Dialog */}
      <Dialog open={addParticipantOpen} onClose={handleCloseAddParticipant}>
        <DialogTitle>Add Participant</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: '350px' }}>
          {isLoadingUsers ? (
            <CircularProgress />
          ) : (
            <>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Invite by email (comma-separated for multiple):
                </Typography>
                <TextField
                  label="Email(s)"
                  variant="outlined"
                  fullWidth
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  placeholder="john@example.com, jane@example.com"
                />
                <Button
                  sx={{ mt: 1 }}
                  variant="contained"
                  color="secondary"
                  onClick={handleInviteByEmail}
                  disabled={inviteByEmailMutation.isLoading}
                >
                  {inviteByEmailMutation.isLoading ? 'Inviting...' : 'Invite by Email'}
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddParticipant}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create New Chat Dialog */}
      <CreateNewChatDialog
        open={newChatOpen}
        onClose={() => setNewChatOpen(false)}
        onCreated={(convoId) => {
          queryClient.invalidateQueries(['messages', 'userSentConversations']);
          setNewChatOpen(false);
          handleSelectConversation(convoId);
        }}
      />

      {/* Update Conversation Details Dialog */}
      <UpdateConversationDetailsDialog
        open={updateDetailsDialogOpen}
        onClose={handleCloseUpdateDetailsDialog}
        conversation={selectedConversation}
      />

      {/* Participants Drawer */}
      <Drawer anchor="right" open={participantsOpen} onClose={handleToggleParticipants}>
        <ParticipantsPanel conversationId={selectedConversationId} onClose={handleToggleParticipants} />
      </Drawer>

      {/* Global Alert Dialog */}
      <AlertDialog alertState={dialogState} />
    </Box>
  );
}